/**
 * The states the browser's notification permission can be in.
 * @type {Readonly<Record<string, string>>}
 */
export const PushPermission = Object.freeze({
  DEFAULT: "default",
  GRANTED: "granted",
  DENIED: "denied",
});

const SERVICE_WORKER_PATH = "/sw.js";

/**
 * Thrown when the browser will not let this site show notifications.
 */
export class PushPermissionError extends Error {
  /**
   * Builds the error from the permission the browser reported.
   * @param {string} permission The PushPermission value the browser answered
   */
  constructor(permission) {
    super(`Notification permission was ${permission}`);
    this.name = "PushPermissionError";
    this.permission = permission;
  }
}

/**
 * Whether this browser can receive push notifications at all.
 *
 * On iOS the Push API is reachable only from a home-screen web app, so a Safari
 * tab reports no PushManager and this is false until the user installs.
 * @returns {boolean} True when service workers, push and notifications all exist
 */
export function isPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * The browser's current notification permission.
 * @returns {string} One of the PushPermission values
 */
export function currentPermission() {
  if (!isPushSupported()) return PushPermission.DENIED;

  return Notification.permission;
}

/**
 * The IANA timezone name this device is in, e.g. "America/New_York".
 *
 * The name is stored rather than an offset so that daylight saving corrects
 * itself and only genuine relocation has to be detected.
 * @returns {string} The IANA timezone name
 */
export function currentTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * A short human-readable name for this device, so a list of subscriptions is
 * something a person can tell apart.
 * @returns {string} The device label
 */
export function currentDeviceLabel() {
  const platform = navigator.userAgentData?.platform;
  const brand = navigator.userAgentData?.brands?.find(
    (entry) => !/not.a.brand/i.test(entry.brand),
  )?.brand;

  if (platform && brand) return `${brand} on ${platform}`;
  if (platform) return platform;

  return navigator.platform || "This device";
}

/**
 * Registers the push service worker.
 *
 * The worker caches nothing and intercepts no fetches. Registration is not
 * permission: the browser is not prompted until the user asks to be subscribed.
 * @returns {Promise<ServiceWorkerRegistration|null>} The registration, or null when unsupported
 */
export async function registerPushWorker() {
  if (!isPushSupported()) return null;

  try {
    return await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  } catch (error) {
    console.error("Failed to register the push service worker", error);
    return null;
  }
}

/**
 * This origin's service worker registration, or null when it has none.
 *
 * navigator.serviceWorker.ready answers the same question and must not be used
 * for it: it never rejects and stays pending forever when registration failed,
 * so awaiting it on the sign-out path would leave the user unable to sign out
 * with nothing to show for it.
 * @returns {Promise<ServiceWorkerRegistration|null>} The registration, or null
 */
async function currentRegistration() {
  if (!isPushSupported()) return null;

  return (await navigator.serviceWorker.getRegistration()) || null;
}

/**
 * Converts a base64url VAPID key into the byte array subscribe() expects.
 * @param {string} base64Url The base64url-encoded key
 * @returns {Uint8Array} The decoded key
 */
function urlBase64ToUint8Array(base64Url) {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);

  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

/**
 * Reshapes a browser PushSubscription into the fields WSC stores.
 *
 * The spec names the two keys p256dh and auth; they are stored under names that
 * say what they are.
 * @param {PushSubscription} subscription The browser's subscription
 * @returns {object} The subscription as WSC's proto shape
 */
function toStoredSubscription(subscription) {
  const { endpoint, keys } = subscription.toJSON();

  return {
    endpoint,
    publicKey: keys?.p256dh || "",
    authSecret: keys?.auth || "",
    timezone: currentTimezone(),
    deviceLabel: currentDeviceLabel(),
    enabled: true,
  };
}

/**
 * This device's existing push subscription, if it has one.
 * @returns {Promise<PushSubscription|null>} The browser subscription, or null
 */
export async function existingBrowserSubscription() {
  const registration = await currentRegistration();
  if (!registration) return null;

  return registration.pushManager.getSubscription();
}

/**
 * The endpoint identifying this device, used to pick this device out of the
 * account's list of subscriptions without storing an id locally.
 * @returns {Promise<string|null>} The endpoint, or null when not subscribed
 */
export async function currentEndpoint() {
  const subscription = await existingBrowserSubscription();

  return subscription?.endpoint || null;
}

/**
 * Whether a subscription was minted with the key the server signs with today.
 *
 * The push service accepts only sends signed by the private half of the pair the
 * subscription was minted against, so a device registered under a previous pair
 * is permanently undeliverable while still looking registered from both ends.
 * A browser too old to report the key it used is taken at its word rather than
 * re-minted on every enable.
 * @param {PushSubscription} subscription The browser's subscription
 * @param {string} vapidPublicKey The server's VAPID public key
 * @returns {boolean} True when the keys match, or the browser will not say
 */
function matchesServerKey(subscription, vapidPublicKey) {
  const applied = subscription.options?.applicationServerKey;
  if (!applied) return true;

  const minted = new Uint8Array(applied);
  const current = urlBase64ToUint8Array(vapidPublicKey);

  return (
    minted.length === current.length &&
    minted.every((byte, index) => byte === current[index])
  );
}

/**
 * Asks permission if needed and subscribes this device to push.
 *
 * Must be called from a user gesture: iOS refuses the permission prompt
 * otherwise. userVisibleOnly is required by Chrome and is a promise that every
 * push produces a notification, which the digest job honours by never sending
 * for an empty day.
 * @param {string} vapidPublicKey The server's VAPID public key
 * @returns {Promise<object>} The subscription in WSC's shape
 * @throws {PushPermissionError} When the browser refuses permission
 * @throws {Error} When the worker never registered
 */
export async function subscribeThisDevice(vapidPublicKey) {
  const permission = await Notification.requestPermission();
  if (permission !== PushPermission.GRANTED) {
    throw new PushPermissionError(permission);
  }

  const registration = await currentRegistration();
  if (!registration) {
    throw new Error("The push service worker is not registered");
  }

  const existing = await registration.pushManager.getSubscription();

  if (existing && matchesServerKey(existing, vapidPublicKey)) {
    return toStoredSubscription(existing);
  }

  await existing?.unsubscribe();

  return toStoredSubscription(
    await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }),
  );
}

/**
 * Drops this device's browser-side subscription.
 *
 * The server row is deleted separately; this is the half that stops the push
 * service from holding an endpoint nothing will ever send to.
 * @returns {Promise<void>} Resolves once the subscription is gone
 */
export async function unsubscribeThisDevice() {
  const subscription = await existingBrowserSubscription();

  await subscription?.unsubscribe();
}
